import { createFileRoute } from "@tanstack/react-router"
import React from "react"

import JsonPrettyPrint from "@/components/JsonPrettyPrint"
import DevicesTable from "@/components/tables/DevicesTable"
import { useDeviceIds, useDevicesQueries, useSubscribers } from "@/hooks"
import { orderDevicesByUsername, pageUsernames } from "@/utils/deviceOrder"
import { parseDevicesSearch } from "@/utils/parseDevicesSearch"

export const Route = createFileRoute("/_auth/devices/")({
  validateSearch: parseDevicesSearch,
  component: () => <DevicesPage />,
  staticData: {
    title: "Devices",
  },
})

const DevicesPage = () => {
  const {
    offset = 0,
    limit = 20,
    subscriberId,
    partnerId,
    search,
    sortBy,
    order = "desc",
  } = Route.useSearch()
  // No sortBy means no sort param, letting the API apply its default ordering.
  // Both list calls get the same value, which is what keeps paging consistent.
  const sort = sortBy ? { sortBy, order } : undefined

  const subscribersQuery = useSubscribers(undefined, partnerId)
  let subscriberIds: Array<string> | undefined
  if (subscriberId) {
    subscriberIds = [subscriberId]
  } else if (partnerId) {
    subscriberIds = subscribersQuery.data?.map((s) => s.id)
  }

  // When filtering by partner, wait until subscriber IDs are resolved before fetching devices.
  // Without this gate, useDeviceIds would fire with subscriberIds=undefined and fetch all devices.
  const deviceQueriesEnabled = !partnerId || subscribersQuery.isSuccess

  // Fetch all device usernames — lightweight (username-only), used for total
  // count and paging. The search filter is now applied server-side (name,
  // NTRIP username, and comment), so deviceIds is already the filtered set.
  const {
    data: deviceIds,
    isLoading: deviceIdsLoading,
    error: deviceIdsError,
  } = useDeviceIds(subscriberIds, sort, search, {
    enabled: deviceQueriesEnabled,
  })

  const usernamesToFetch = pageUsernames(deviceIds ?? [], offset, limit)

  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
  } = useDevicesQueries(subscriberIds, usernamesToFetch, sort)

  if (deviceIdsError || devicesError) {
    return <JsonPrettyPrint json={deviceIdsError} />
  }

  // The username list from useDeviceIds is server-sorted; make the rendered
  // rows follow that exact order (record fetches may return per-chunk order).
  const orderedDevices = orderDevicesByUsername(devices, usernamesToFetch)

  return (
    <DevicesTable
      devices={orderedDevices}
      isLoading={deviceIdsLoading || devicesLoading}
      limit={limit}
      linkTo="/devices"
      search={search}
      offset={offset}
      subscriberId={subscriberId}
      totalCount={(deviceIds ?? []).length}
      exportSubscriberIds={subscriberIds}
      // Only the partner filter's subscriber IDs can still be resolving here:
      // the search filter is applied server-side now, so the export request
      // sends `search` straight through as `search_string` and has nothing of
      // its own to wait on. See the matching deviceQueriesEnabled gate above.
      exportFiltersResolving={!deviceQueriesEnabled}
    />
  )
}
